const BOT_LINK = "https://t.me/instant_email_bot";

export const partnerPages = [
  {
    slug: "staymii-hr-job-application-form",
    title: "Free Job Application Form with Instant Telegram Notifications | StayMii HR + TelegramEmailBot",
    metaTitle: "Free Job Application Form Online - Instant Telegram Alerts | StayMii HR + TelegramEmailBot",
    metaDescription: "Free job application form with instant Telegram notifications via TelegramEmailBot. Centralize all applicants from every job portal into one platform. Free signup, no credit card. StayMii HR + @instant_email_bot.",
    keywords: [
      "free job application form",
      "free job application form online",
      "free telegram job application form",
      "free all in one job application forms",
      "free signup for hr job application form",
      "staymii hr",
      "staymii human resource",
      "job application form template free",
      "online job application form",
      "applicant tracking system free",
      "hr application form",
      "telegram job alerts",
      "telegramemailbot",
      "@instant_email_bot",
      "free hr software",
      "job application tracker",
      "centralized job applications",
      "hiring form free",
      "recruitment form online free"
    ],
    h1: "Free Job Application Form with Instant Telegram Notifications",
    subtitle: "Give applicants a simple form link. Get every submission instantly in Telegram. Track all candidates from every job portal in one place. Free forever — no credit card, no catch.",
    content: `
      <h2>What is StayMii HR?</h2>
      <p><strong>StayMii HR</strong> is a free, all-in-one job application platform that lets companies collect, track, and manage job applications from a single dashboard. Instead of juggling emails from JobStreet, Indeed, LinkedIn, and your company website — StayMii HR centralizes every applicant into one platform.</p>
      <p>Create your free job application form in 30 seconds. Share the link on any job portal, social media, or your careers page. Every time a candidate fills out the form, you get notified instantly — either by email or directly in <strong>Telegram</strong> via <strong>TelegramEmailBot</strong>.</p>

      <h2>Why Use a Free Job Application Form?</h2>
      <p>Most companies still collect job applications through email. The result?</p>
      <ul>
        <li>Resumes scattered across multiple inboxes</li>
        <li>Applications lost in spam folders</li>
        <li>No central record of who applied and when</li>
        <li>Delayed responses — good candidates get hired elsewhere</li>
        <li>Different formats from different job portals</li>
        <li>No way for the whole HR team to see applications together</li>
      </ul>
      <p><strong>StayMii HR solves all of this.</strong> One form link. One platform. Every applicant in one place. And with TelegramEmailBot, your HR team gets notified the <em>second</em> someone applies.</p>

      <h2>How It Works — 4 Simple Steps</h2>
      <h3>Step 1: Sign Up for StayMii HR (Free, 30 Seconds)</h3>
      <p>Go to <a href="https://staymii.com/staymii-hr" target="_blank" rel="noopener noreferrer"><strong>staymii.com/staymii-hr</strong></a> and create your free account. No credit card required. No trial period for the form itself — it's free forever.</p>

      <h3>Step 2: Create Your Job Application Form</h3>
      <p>Set up your company profile and create your job application form. Customize the fields you need — personal details, work experience, education, resume upload, cover letter, and more. StayMii HR gives you a shareable link that works on any device.</p>

      <h3>Step 3: Connect TelegramEmailBot for Instant Alerts</h3>
      <p>This is where the magic happens. To get <strong>instant Telegram notifications</strong> every time someone applies:</p>
      <ol>
        <li>Open Telegram and start <strong><a href="https://t.me/instant_email_bot" target="_blank" rel="noopener noreferrer">@instant_email_bot</a></strong></li>
        <li>You'll get a unique email address instantly (e.g., <code>abc123@telegramemailbot.com</code>)</li>
        <li>Copy this email address</li>
        <li>In StayMii HR, go to <strong>Settings → Telegram Email</strong></li>
        <li>Paste your TelegramEmailBot email address</li>
        <li>Done! Every new application triggers an instant Telegram notification</li>
      </ol>
      <p>You can also add the bot to a <strong>Telegram group</strong> so your entire HR team gets notified simultaneously.</p>

      <h3>Step 4: Share Your Form & Track Applicants</h3>
      <p>Share your StayMii HR form link on:</p>
      <ul>
        <li><strong>Job portals:</strong> JobStreet, Indeed, LinkedIn, Glassdoor, Monster</li>
        <li><strong>Social media:</strong> Facebook, Instagram, Twitter, WhatsApp groups</li>
        <li><strong>Your website:</strong> Embed or link from your careers page</li>
        <li><strong>Job ads:</strong> Include the link in any job advertisement</li>
        <li><strong>QR codes:</strong> Print and post at your office or events</li>
      </ul>
      <p>No matter where candidates find your job post — they all fill out the same form, and all applications land in one place.</p>

      <h2>Key Features of StayMii HR</h2>

      <h3>Free Job Application Form</h3>
      <p>Create unlimited job application forms at no cost. Each form gets a unique shareable link that works on desktop, tablet, and mobile. Candidates don't need to create an account or download anything — they just fill out the form and submit.</p>

      <h3>Centralized Applicant Tracking</h3>
      <p>Every application from every source lands in your StayMii HR dashboard. Whether someone applied through JobStreet, your website, or a LinkedIn post — all applications are in one place. No more switching between platforms or searching through emails.</p>

      <h3>Instant Telegram Notifications via TelegramEmailBot</h3>
      <p>This is the killer feature. Connect <strong>TelegramEmailBot (@instant_email_bot)</strong> and get push notifications in Telegram within seconds of a new application. Your HR team can:</p>
      <ul>
        <li>See new applications instantly on their phone</li>
        <li>Review candidate details right from Telegram</li>
        <li>Share alerts with the whole team via a Telegram group</li>
        <li>Respond to strong candidates within minutes instead of days</li>
      </ul>

      <h3>Login & Review Applications</h3>
      <p>Log into your StayMii HR dashboard anytime to review all applications in detail. View resumes, filter by date or position, track application status, and manage your hiring pipeline. All data is stored securely and accessible from any device.</p>

      <h3>Multi-Portal Integration</h3>
      <p>Use one StayMii HR form link across all job portals and platforms. Instead of managing separate applications from JobStreet, Indeed, LinkedIn, and your website — centralize everything. One link, one dashboard, one notification system.</p>

      <h3>Mobile-Friendly Forms</h3>
      <p>StayMii HR forms are fully responsive. Candidates can apply from their phone in under 2 minutes. No zooming, no broken layouts — just a clean, professional application experience.</p>

      <h2>Who Should Use StayMii HR?</h2>

      <h3>Small & Medium Businesses</h3>
      <p>You don't need expensive HR software to manage hiring. StayMii HR gives you a professional job application system for free. Perfect for companies hiring 1-50 positions who need a simple, effective solution.</p>

      <h3>Hotels & Hospitality</h3>
      <p>Hotels, restaurants, and resorts with high staff turnover need fast hiring. StayMii HR + Telegram notifications means your HR team sees every application instantly — even during busy shifts.</p>

      <h3>Retail & F&B</h3>
      <p>Retail chains and food & beverage businesses often advertise on multiple platforms. StayMii HR centralizes all applications so nothing falls through the cracks.</p>

      <h3>Startups & Growing Teams</h3>
      <p>When you're growing fast, you can't afford slow hiring. StayMii HR with Telegram alerts ensures you respond to good candidates before your competitors do.</p>

      <h3>Recruitment Agencies</h3>
      <p>Agencies managing multiple job openings across multiple clients benefit from centralized tracking. One form per position, all applications in one dashboard.</p>

      <h2>StayMii HR vs Other HR Tools</h2>
      <table>
        <thead><tr><th>Feature</th><th>StayMii HR</th><th>Google Forms</th><th>Paid ATS Tools</th></tr></thead>
        <tbody>
          <tr><td>Price</td><td><strong>Free</strong></td><td>Free</td><td>$50-500/month</td></tr>
          <tr><td>Job application specific</td><td>Yes</td><td>No (generic forms)</td><td>Yes</td></tr>
          <tr><td>Applicant tracking dashboard</td><td>Yes</td><td>No</td><td>Yes</td></tr>
          <tr><td>Instant Telegram alerts</td><td>Yes (via TelegramEmailBot)</td><td>No</td><td>No</td></tr>
          <tr><td>Mobile-friendly forms</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Centralize from all portals</td><td>Yes</td><td>No</td><td>Some</td></tr>
          <tr><td>Resume upload</td><td>Yes</td><td>Limited</td><td>Yes</td></tr>
          <tr><td>Team notifications</td><td>Telegram group alerts</td><td>Email only</td><td>Email/Slack</td></tr>
          <tr><td>Setup time</td><td>30 seconds</td><td>5-10 minutes</td><td>Hours-days</td></tr>
          <tr><td>Credit card required</td><td>No</td><td>No</td><td>Usually yes</td></tr>
        </tbody>
      </table>

      <h2>How TelegramEmailBot Powers StayMii HR Notifications</h2>
      <p><strong><a href="https://telegramemailbot.com" target="_blank" rel="noopener noreferrer">TelegramEmailBot</a></strong> is a Telegram bot that gives you an instant email address. Any email sent to that address appears in your Telegram chat within 30-60 seconds.</p>
      <p>When you connect TelegramEmailBot to StayMii HR:</p>
      <ol>
        <li>A candidate fills out your job application form</li>
        <li>StayMii HR sends a notification email to your TelegramEmailBot address</li>
        <li>Within seconds, the notification appears in your Telegram (or team group)</li>
        <li>Your HR team sees the application details immediately</li>
        <li>You can log into StayMii HR for the full application details</li>
      </ol>
      <p>The TelegramEmailBot notification includes an <strong>AI-powered summary</strong> of the application, so you can quickly assess candidates without opening the full form.</p>

      <h3>Simple, Transparent Pricing</h3>
      <p><strong>StayMii HR</strong> is completely free — no limits, no trial period for the application form.</p>
      <p><strong>TelegramEmailBot</strong> is <strong>$4.99 one-time</strong> for a permanent inbox with 20 complimentary notifications. Need unlimited alerts plus AI-powered application summaries? Upgrade to <strong>Inbox Storage &amp; AI at $14.99/year</strong> — less than $1.25/month. No credit card required to start.</p>

      <h2>Step-by-Step Setup Guide</h2>
      <h3>Setting Up StayMii HR</h3>
      <ol>
        <li>Visit <a href="https://staymii.com/staymii-hr" target="_blank" rel="noopener noreferrer">staymii.com/staymii-hr</a></li>
        <li>Click "Sign Up" — enter your company name and email</li>
        <li>Create your first job application form</li>
        <li>Customize the form fields for your job position</li>
        <li>Copy the shareable form link</li>
      </ol>

      <h3>Connecting Telegram Notifications</h3>
      <ol>
        <li>Open Telegram on your phone or desktop</li>
        <li>Search for <strong>@instant_email_bot</strong> and tap /start</li>
        <li>The bot gives you an email address (e.g., <code>xyz789@telegramemailbot.com</code>)</li>
        <li>In StayMii HR, go to Settings → Telegram Email</li>
        <li>Paste the email address and save</li>
        <li>Test by submitting a test application — you should get a Telegram notification in seconds</li>
      </ol>

      <h3>Setting Up Team Alerts</h3>
      <ol>
        <li>Create a Telegram group with your HR team members</li>
        <li>Add @instant_email_bot to the group</li>
        <li>The bot assigns a group-specific email address</li>
        <li>Use this group email in StayMii HR settings</li>
        <li>Now every team member gets every application notification</li>
      </ol>

      <h2>Real-World Use Case: Hotel Hiring</h2>
      <p>A boutique hotel in Penang needed to hire 5 positions simultaneously — front desk, housekeeping, F&B, maintenance, and a manager. They posted on JobStreet, Facebook, and their website.</p>
      <p><strong>Before StayMii HR:</strong> Applications came to 3 different email addresses. The HR manager checked email twice a day. Good candidates were already hired by other hotels by the time they responded.</p>
      <p><strong>After StayMii HR + TelegramEmailBot:</strong> One form link shared everywhere. All applications in one dashboard. Instant Telegram notifications to the HR team group. Response time went from 2 days to under 30 minutes. They filled all 5 positions in 2 weeks.</p>

      <h2>Frequently Asked Questions</h2>

      <h3>Is StayMii HR really free?</h3>
      <p>Yes. The job application form, applicant tracking dashboard, and candidate management are completely free. No credit card, no trial period, no hidden fees.</p>

      <h3>Is the Telegram notification free?</h3>
      <p>TelegramEmailBot offers a Starter Email at $4.99 one-time. After that, it's $14.99/year for unlimited notifications. The StayMii HR platform itself is always free.</p>

      <h3>Can I use it without Telegram?</h3>
      <p>Yes. StayMii HR works independently — you can receive email notifications and log in to view applications. Telegram notifications via TelegramEmailBot are an optional (but highly recommended) add-on.</p>

      <h3>How many job application forms can I create?</h3>
      <p>There's no limit. Create as many forms as you need for different positions.</p>

      <h3>Can candidates upload resumes?</h3>
      <p>Yes. Candidates can upload resumes, cover letters, and other documents through the form.</p>

      <h3>Can my whole HR team see the applications?</h3>
      <p>Yes. Log into StayMii HR with your team credentials to view all applications. For instant alerts, add TelegramEmailBot to a Telegram group with your team.</p>

      <h3>Does it work on mobile?</h3>
      <p>Yes. Both the application form (for candidates) and the dashboard (for HR) are fully mobile-responsive.</p>

      <h3>How fast do Telegram notifications arrive?</h3>
      <p>Within 30-60 seconds of a candidate submitting the form.</p>

      <h3>Can I use one form link on multiple job portals?</h3>
      <p>Absolutely. That's the whole point — one link, share everywhere, all applications centralized in one place.</p>

      <h3>Is my data secure?</h3>
      <p>Yes. StayMii HR uses secure servers and encrypted connections. Candidate data is only accessible to your authorized team members.</p>
    `,
    faqs: [
      { q: "Is StayMii HR free?", a: "Yes, completely free. No credit card, no trial period for the job application form and tracking dashboard." },
      { q: "How do I get Telegram notifications for job applications?", a: "Connect TelegramEmailBot (@instant_email_bot) to StayMii HR. Start the bot in Telegram, get your email address, paste it in StayMii HR settings. Get instant alerts for every new application." },
      { q: "Can I centralize applications from multiple job portals?", a: "Yes. Use one StayMii HR form link across JobStreet, Indeed, LinkedIn, Facebook, and your website. All applications land in one dashboard." },
      { q: "Is the Telegram notification free?", a: "Starter Email at $4.99 one-time, then $14.99/year for unlimited notifications via TelegramEmailBot." },
      { q: "Can my whole team see applications?", a: "Yes. Log into StayMii HR dashboard or add TelegramEmailBot to a Telegram group for team-wide instant alerts." },
      { q: "How fast do notifications arrive?", a: "Within 30-60 seconds of a candidate submitting the application form." },
    ],
    relatedSlugs: ["telegram-email-bot", "receive-email-on-telegram", "email-to-telegram-alerts"],
  },
];

export default partnerPages;
